  import { defineComponent, type PropType } from "vue";
  import EtablissementSummary from '@/extend/controls/etablissement/EtablissementSummary';
  import NumeroObjetSummary from '@/extend/controls/numeroObjet/NumeroObjetSummary';
  import ProjetNom from '@/extend/controls/projet/ProjetNom';
  import Projet from '@/extend/classes/Projet';
  import { FieldStatus, EditFieldTheme, SectionStatus } from '@/framework/base/components/FieldInfo';
  import { Alerts } from '@/framework/base/helpers/Alerts'; 
  import { storeProjet } from '@/generated/stores';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import { Extended } from '@/framework/base/classes/Schema';
  import ButtonPlus from '@/framework/base/components/ButtonPlus.vue';
  import Route from '@/generated/classes/Route';
  import { Fiches, Sections } from '@/generated/classes/AppSchema';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import GraphControl from '@/framework/graph/components/GraphControl.vue';
  import { GraphAnswer, GraphMinimum } from '@/generated/classes/GraphData';
  export default defineComponent({
    components: {
      SectionHeader,
      LoaderButton,
      ButtonPlus,
      GraphControl,
      EtablissementSummary,
      NumeroObjetSummary,
      ProjetNom,
    },

    props: {
      projet: {
        type: Object as PropType<Projet>,
        required: true
      },
      showButton: {
        type: Boolean,
      },
      autoSave: {
        type: Boolean,
      },
      fieldOnly: {
        type: Boolean,
      },
      extended: {
        type: Boolean,
      },
      autoClear: {
        type: Boolean,
      },
      dialog: {
        type: Boolean,
      },
      closeSection: {
        type: Boolean,
      },
      showMissing: {
        type: Boolean,
      },
      moduleId: {
        type: Number,
      },
      theme: {
        type: Object as PropType<EditFieldTheme>,
      },
      fiche: {
        type: String,
      },
      fromSection: {
        type: String,
      },
      requiredAttributs: {
        type: Array as PropType<string[]>,
      },
    },

    beforeUnmount (): void {
      if (!this.closeSection) {
        this.beforeClose();
      }
    },

    data() {
      return {
        graphStatus: new FieldStatus(false, false, 0),
    toggleEtablissementId: false,
        etablissementIdStatus: new FieldStatus(false, false, 0),
        strEtablissementId: '',
    toggleNumeroObjetId: false,
        numeroObjetIdStatus: new FieldStatus(false, false, 0),
        strNumeroObjetId: '',
        nomStatus: new FieldStatus(false, false, 0),
        strNom: '',
        toggleGraph: false,
        refProjet: new Projet,
        cloneProjet: new Projet,
      projetSaving: false,
        saving: false,
        restoring: false,
        inDialog: false,
        updated: false,
        error: false,
        saveLoading: false,
        saveFailed: false,
        allowClearRef: true,
        objType: ObjTypes.PROJET,
        status: new SectionStatus(false, false, false),
        autoClose: true,
        closing: false
      }
    },
    computed: {
      isExtended (): boolean {
        return this.extended;
      },

      exist (): boolean {
        return this.editProjet.id != 0
      },

      extendIcon (): string {
        return this.extended ? 'fas fa-circle-minus' : 'fas fa-circle-plus';
      },

      saveButton (): boolean {
        return (this.showButton == undefined || this.showButton == true) && !this.allReadOnly;
      },

      editProjet: {
        get(): Projet {
          if (this.closeSection) {
            this.beforeClose();
          } else {
            if (this.projet != undefined) {
              if (this.projet.id == 0) {
                Alerts.debug('CrudProjet : les objets passés en paramètre doivent être issus du store (id <> 0)');
              }
              if (this.cloneProjet.id != this.projet.id && !this.saving) {
                this.handleProjetChange();
              }
            }
          }
          return this.refProjet;
        },
        set(value: Projet) {
          this.refProjet = value;
        }
      },

      canCreate (): boolean {
        return this.refProjet.id > 0;
      },

      canDelete (): boolean {
        return this.refProjet.canDelete && this.refProjet.id > 0;
      },

      updatedAndValid (): boolean {
        return this.updated && !this.error;
      },
      graphAnswers: {
        get(): GraphAnswer[] {
          return this.editProjet.graphAnswers;
        },
        set(value: GraphAnswer[]) {
          this.editProjet.graphAnswers = value;
        }
      },

      clearAuto (): boolean {
        return (this.autoClear == undefined || this.autoClear == true) && this.allowClearRef;
      },

    resetEtablissementId (): boolean {
      return this.toggleEtablissementId
    },

      etablissementId: {
        get(): number {
          return this.editProjet.etablissementId;
        },
        set(value: number) {
          this.editProjet.etablissementId = value;
          this.fieldUpdated('ETABLISSEMENT_ID', value);
        }
      },

    resetNumeroObjetId (): boolean {
      return this.toggleNumeroObjetId
    },

      numeroObjetId: {
        get(): number {
          return this.editProjet.numeroObjetId;
        },
        set(value: number) {
          this.editProjet.numeroObjetId = value;
          this.fieldUpdated('NUMERO_OBJET_ID', value);
        }
      },


      nom: {
        get(): string {
          return this.editProjet.nom;
        },
        set(value: string) {
          this.editProjet.nom = value;
          this.fieldUpdated('NOM', value);
        }
      },


      allReadOnly (): boolean {
        const res = this.projet.canUpdate && (
        (this.isDisplayed('ETABLISSEMENT_ID') && !this.readOnly('ETABLISSEMENT_ID')) ||
        (this.isDisplayed('NUMERO_OBJET_ID') && !this.readOnly('NUMERO_OBJET_ID')) ||
        (this.isDisplayed('NOM') && !this.readOnly('NOM')) ||
        (this.isDisplayed('GRAPH_CONTROL') && !this.readOnly('GRAPH_CONTROL')));
        return !res;
      },

      fields (): string[] {
        return ['ETABLISSEMENT_ID', 'NUMERO_OBJET_ID', 'NOM'];
      },
    },

    methods: {
      handleProjetChange() : void {
        // Edited object changed
        this.saveFailed = false;
        this.toggleGraph = !this.toggleGraph;
        if (this.autoSave && this.updatedAndValid) {
          if (!this.inDialog && this.checkFields()) {
            // Dialogue to save or cancel changes
            this.inDialog = true;
            this.dialogSaveOrCancel().then(() => {
              this.createRef(this.projet);
              this.inDialog = false;
                  })
          } else if (!this.restoring && !this.inDialog) {
            this.restoreEdited();
            this.createRef(this.projet);
          }
        } else if (!this.inDialog) {
          if (this.cloneProjet.id < 0) {
            this.clearRef();
          } else if (this.updated) {
            this.restoreEdited();
          }
          this.createRef(this.projet);
        }
      },
      beforeClose (): void {
        if (!this.closing) {
          this.closing = true;
          if (this.autoSave && this.updatedAndValid && this.checkFields()) {
            this.$emit('saveOrCancel');
            this.dialogSaveOrCancel();
          } else {
            if (this.autoSave && this.updated) {
              this.restoreEdited();
            }
            this.$emit('cancelled');
          }
          this.clearRef();
          }
      },

      setExtended (extended: Extended): void {
         this.$emit('extended', extended);
      },

      // eslint-disable-next-line
      isDisplayed (value: string): boolean {
        return true;
      },

      // eslint-disable-next-line
      required (value: string): boolean {
        return false;
      },

      // eslint-disable-next-line
      readOnly (value: string): boolean {
        return false;
      },

      save (): void {
        if (event != undefined) {
          event.stopPropagation();
        }
        this.saveLoading = true;
        this.saveFailed = false;
        if (this.checkFields()) {
          this.saveObj();
        } else {
        this.saveLoading = false;
          this.saveFailed = true;
        }
      },

      setUpdated (): void {
        const updated = (this.etablissementIdStatus != undefined && this.etablissementIdStatus.updated) ||
          (this.numeroObjetIdStatus != undefined && this.numeroObjetIdStatus.updated) ||
          (this.nomStatus != undefined && this.nomStatus.updated) ||
          (this.graphStatus != undefined && this.graphStatus.updated);
        if (updated != this.updated) {
          this.updated = updated;
          this.status.updated = updated;
          this.status.missing = this.hasMissing();
          if (!this.closing) {
            this.$emit('status', this.status);
          }
          this.$emit('updated', this.projet);
        }
      },

      setError (): void {
        const error = this.etablissementIdStatus.error ||
        this.numeroObjetIdStatus.error ||
        this.nomStatus.error ||
          this.graphStatus.error;
        if (error != this.error) {
          if (!error) {
            this.saveFailed = false;
          }
          this.error = error;
          this.status.error = error;
          if (!this.closing) {
            this.$emit('status', this.status);
          }
          this.$emit('updated', this.projet);
        }
      },

      setMinimum(value: GraphMinimum): void {
        this.editProjet.graphMinimum = value;
      },

      statusGraph(value: FieldStatus): void {
        this.graphStatus = value;
        this.setUpdated();
        this.setError();
      },

      // Clear non saved new objects when leaving
      clearRef (): void {
        if (this.clearAuto && this.refProjet.id < 0) {
          storeProjet.mProjetDeleted(this.refProjet.id);
        }
      },

      // Dialog to save or ignore changes when loosing focus or changing edited item
      dialogSaveOrCancel (): Promise<boolean> {
        return new Promise((resolve) => {
          if (!this.error) {
            Alerts.saveOrCancel()
              .then((result: boolean) => {
                if (result) {
                  this.saveObj();
                } else {
                  if (this.refProjet.id <= 0) {
                    this.deleteObj();
                  } else {
                    this.restoreEdited();
                  }
                  this.$emit('cancelled');
                }
                resolve(result);
              })
          } else {
            this.restoreEdited();
          }
        })
      },

      // Save object and subobjects referenced by foreignClass keys
      saveObj (): void {
        this.saving = true;
        if (
          this.saveProjet()
          ) {
          this.toggleGraph = !this.toggleGraph;
          this.saving = false;
          this.saveLoading = false;
          this.projetSaving = false;
          this.createRef(this.refProjet);
          this.$emit('updated', this.refProjet);
          if (this.projet.id > 0) {
            this.$emit('saved', this.refProjet);
          } else {
            this.updated = true;
          }
          if (this.autoClose) {
            this.saveFields();
            this.$emit('closeDialog');
          }
        } else {
          if (this.refProjet.failed) {
          this.saveLoading = false;
            this.projetSaving = false;
            this.refProjet.failed = false;
          }
        }
      },

      saveProjet (): boolean {
        if (!this.refProjet.compareProjet(this.cloneProjet)) {
          if (!this.projetSaving) {
            this.projetSaving = true;
          if (this.refProjet.id > 0) {
            storeProjet.updateProjet(this.refProjet)
              .then((data: Projet) => {
                if (!data.failed) {
                  this.refProjet = storeProjet.getProjet(data.id);
                  this.cloneProjet = data.cloneProjet();
                } else {
                  this.refProjet.failed = true;
                }
                this.saveObj();
              })
              .catch(() => {
                this.restoreEdited();
                this.projetSaving = false;
                this.saveLoading = false;
              })
          }
            if (this.refProjet.id <= 0) {
              storeProjet.createProjet(this.refProjet)
                .then((data: Projet) => {
                  if (!data.failed) {
                    this.refProjet = storeProjet.getProjet(data.id);
                    this.cloneProjet = data.cloneProjet();
                  } else {
                this.refProjet.failed = true;
                  }
                  this.saveObj();
                })
                .catch(() => {
                  this.restoreEdited();
                    this.projetSaving = false;
                })
            }
          }
          return false;
        } else {
          return true;
        }
      },

      // Delete object
      deleteObj (): void {
        if (this.refProjet.id <= 0) {
          storeProjet.mProjetDeleted(this.refProjet.id);
        } else {
          storeProjet.deleteProjet(this.refProjet);
        }
      },

      statusEtablissementId (value: FieldStatus): void {
        this.etablissementIdStatus = value;
        this.setUpdated();
        this.setError();
      },
      statusNumeroObjetId (value: FieldStatus): void {
        this.numeroObjetIdStatus = value;
        this.setUpdated();
        this.setError();
      },
      statusNom (value: FieldStatus): void {
        this.nomStatus = value;
        this.setUpdated();
        this.setError();
      },

      deleteProjet(): void {
        this.$emit('deleteProjet',  this.projet);
      },

    addProjet(): void {
      this.$emit('addProjet',  this.projet);
    },

      // Restore object
      restoreEdited (): void {
        if (this.cloneProjet != undefined) {
          this.restoring = true;
          this.refProjet.nom = this.cloneProjet.nom;
          this.restoring = false;
        }
      },


    // Clone and clear edited
      createRef (value: Projet): void {
        this.clearRef();
        this.editProjet = value;
        this.cloneProjet = value.cloneProjet();
        this.etablissementIdStatus.clear(value.id);
        this.numeroObjetIdStatus.clear(value.id);
        this.nomStatus.clear(value.id);
        this.graphStatus.clear(value.id);
        this.status.clear();
      },

    checkFields (): boolean {
        // @ts-expect-error : generated method
        if (this.isDisplayed('ETABLISSEMENT_ID') && this.$refs.ETABLISSEMENT_ID) this.$refs.ETABLISSEMENT_ID.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('NUMERO_OBJET_ID') && this.$refs.NUMERO_OBJET_ID) this.$refs.NUMERO_OBJET_ID.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('NOM') && this.$refs.NOM) this.$refs.NOM.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('GRAPH_CONTROL') && this.$refs.GRAPH_CONTROL) this.$refs.GRAPH_CONTROL.touch();
        return !this.error;
      },

      missing (value: string): boolean {
        return this.hasMissing(value);
      },

      hasMissing (value = ''): boolean {
        let missing = false;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'ETABLISSEMENT_ID') && this.isDisplayed('ETABLISSEMENT_ID') && this.$refs.ETABLISSEMENT_ID && this.$refs.ETABLISSEMENT_ID.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'NUMERO_OBJET_ID') && this.isDisplayed('NUMERO_OBJET_ID') && this.$refs.NUMERO_OBJET_ID && this.$refs.NUMERO_OBJET_ID.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'NOM') && this.isDisplayed('NOM') && this.$refs.NOM && this.$refs.NOM.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'GRAPH_CONTROL') && this.isDisplayed('GRAPH_CONTROL') && this.$refs.GRAPH_CONTROL && this.$refs.GRAPH_CONTROL.missing()) missing = true;
        return missing;
      },

      saveFields (): void {
        // @ts-expect-error : generated method
        if (this.isDisplayed('ETABLISSEMENT_ID') && this.$refs.ETABLISSEMENT_ID) this.$refs.ETABLISSEMENT_ID.save();
        // @ts-expect-error : generated method
        if (this.isDisplayed('NUMERO_OBJET_ID') && this.$refs.NUMERO_OBJET_ID) this.$refs.NUMERO_OBJET_ID.save();
        // @ts-expect-error : generated method
        if (this.isDisplayed('NOM') && this.$refs.NOM) this.$refs.NOM.save();
      },

      getValue (field: string): string {
         if (field == 'ETABLISSEMENT_ID') return this.etablissementId.toString();
        else  if (field == 'NUMERO_OBJET_ID') return this.numeroObjetId.toString();
        else  if (field == 'NOM') return this.nom.toString();
        else return '';
      },

    // eslint-disable-next-line
    fieldUpdated (field: string, value: unknown): void {
      // To implement
    },

      setFocus (route: Route): boolean {
        let res = false;
        let first = '';
        const attribut = route ? route.attribut : '';
        this.fields.forEach(field => {
          // @ts-expect-error : generated method
          if (!res && this.isDisplayed(field) && !this.readOnly(field) && this.$refs[field].setFocus) {
            if (attribut == field) {
              // @ts-expect-error : generated method
              this.$refs[attribut].setFocus();
              res = true;
            } else if (first == '') {
              first = field;
            }
          }
        })
        // @ts-expect-error : generated method
        if (!res && first != '' && this.$refs[first] && this.$refs[first].setFocus) {
          // @ts-expect-error : generated method
          this.$refs[first].setFocus();
          res = true;
        }
        return res
      },

      closeDialog(): void {
        this.$emit('closeDialog')
      }
    },
  })
